import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Logo, Brand } from "@/components/Logo";
import AdSlot from "@/components/AdSlot";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { getArticle, ARTICLES } from "@/data/articles";
import { api } from "@/lib/api";
import { parseContent, renderInline } from "@/lib/articleFormat";

const fmt = (d) => new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

const absUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

function setMeta(selector, value) {
  if (!value) return;
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", value);
}

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const staticArticle = getArticle(slug);
  const [article, setArticle] = useState(staticArticle || null);
  const [loading, setLoading] = useState(!staticArticle);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (staticArticle) { setArticle(staticArticle); setLoading(false); return; }
    setLoading(true);
    api.get(`/articles/${slug}`)
      .then((r) => setArticle({ ...r.data, sections: parseContent(r.data.content) }))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug, staticArticle]);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = `${article.title} — aiBLACK`;
    const img = absUrl(article.cover_image_url) || "https://aiblack.co/og-image-v2.png";
    setMeta('meta[property="og:title"]', article.title);
    setMeta('meta[name="twitter:title"]', article.title);
    setMeta('meta[property="og:description"]', article.excerpt || article.title);
    setMeta('meta[name="twitter:description"]', article.excerpt || article.title);
    setMeta('meta[property="og:image"]', img);
    setMeta('meta[name="twitter:image"]', img);
    return () => { document.title = prevTitle; };
  }, [article]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]"><div className="flex gap-2"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></div></div>;
  }
  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0f] px-6 text-center">
        <h1 className="font-display font-black text-3xl text-white mb-3">Article not found</h1>
        <button onClick={() => navigate("/articles")} className="text-[#FF5A00] font-bold">← Back to all articles</button>
      </div>
    );
  }

  const midpoint = Math.ceil(article.sections.length / 2);
  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="article-page">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0a0a0f]/80 border-b border-[#2a3040]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <button data-testid="article-back" onClick={() => navigate("/articles")}
            className="flex items-center gap-2 text-sm text-[#9ca3af] hover:text-white transition-colors">
            <ArrowLeft size={18} /> Articles
          </button>
          <Logo size="text-xl" />
          <button onClick={() => navigate("/auth")}
            className="text-sm font-bold text-[#FF5A00] hover:text-[#FF7A33] transition-colors">Sign in</button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 py-12">
        <div className="text-[11px] uppercase tracking-wider text-[#FF5A00] font-bold mb-3">{article.category}</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mb-4">{article.title}</h1>
        <div className="flex items-center gap-3 text-sm text-[#6b7280] mb-8 pb-8 border-b border-[#2a3040]">
          <span className="font-bold text-[#9ca3af]">By <Brand /></span>
          <span className="flex items-center gap-1"><Calendar size={13} /> {fmt(article.date)}</span>
        </div>

        {article.cover_image_url && (
          <img src={absUrl(article.cover_image_url)} alt={article.title} data-testid="article-cover"
            className="w-full rounded-2xl mb-8 border border-[#2a3040] object-cover max-h-[460px]" />
        )}

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {article.sections.map((s, i) => (
            <div key={i}>
              {s.heading && (s.level === 3
                ? <h3 className="font-display font-bold text-xl text-white mt-6 mb-2">{renderInline(s.heading)}</h3>
                : <h2 className="font-display font-bold text-2xl text-white mt-8 mb-3">{renderInline(s.heading)}</h2>)}
              {s.body.map((p, j) => (
                <p key={j} className="text-[15px] sm:text-base text-[#c7ccd6] leading-[1.8] mb-4">{renderInline(p)}</p>
              ))}
              {i === midpoint - 1 && (
                <div className="my-8"><AdSlot isPro={false} variant="banner" testid="article-ad" /></div>
              )}
            </div>
          ))}
        </motion.div>

        <div className="mt-12 bg-[#15151d] border border-[#2a3040] rounded-2xl p-7 text-center">
          <h3 className="font-display font-black text-xl text-white mb-2">Put this into action with <Brand /></h3>
          <p className="text-[#9ca3af] mb-5 text-sm max-w-lg mx-auto">Your AI assistant, real-time news, community, and daily tools — built for us.</p>
          <button data-testid="article-cta" onClick={() => navigate("/auth")}
            className="inline-flex items-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-6 py-3 hover:bg-[#FF7A33] transition-colors">
            Get Started Free <ArrowRight size={16} />
          </button>
        </div>

        {related.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xs uppercase tracking-wider text-[#6b7280] font-bold mb-4">Keep reading</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((a) => (
                <button key={a.slug} data-testid={`related-${a.slug}`} onClick={() => navigate(`/articles/${a.slug}`)}
                  className="text-left bg-[#15151d] border border-[#2a3040] rounded-xl p-4 hover:border-[#FF5A00]/50 transition-colors">
                  <div className="text-[10px] uppercase tracking-wider text-[#FF5A00] font-bold mb-1">{a.category}</div>
                  <div className="font-bold text-white text-sm">{a.title}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
